import React, { useEffect, useState } from 'react';
import { projects, experience, skillGroups, resumes, assetUrl } from './portfolio';
import './App.css';

function Arrow({ diagonal = false, download = false }) {
  return (
    <svg className="arrow-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      {download ? <path d="M12 3v12m-5-5 5 5 5-5M5 16v5h14v-5" /> : diagonal ? <path d="M6 18 18 6M6 6h12v12" /> : <path d="M4 12h16m-6-6 6 6-6 6" />}
    </svg>
  );
}

function ExternalLink({ href, children, className = 'text-link' }) {
  return <a href={href} className={className} target="_blank" rel="noopener noreferrer">{children}<Arrow diagonal /></a>;
}

function SystemIllustration() {
  return (
    <div className="system-illustration" aria-hidden="true">
      <div className="diagram-caption"><span>HARDWARE / SOFTWARE</span><span>FIG. 01</span></div>
      <svg viewBox="0 0 480 380" className="system-drawing" fill="none">
        <g stroke="currentColor" className="circuit-traces" strokeWidth="1.2">
          <path d="M0 95h82l34 34h48M0 190h164M0 285h82l34-34h48M480 95h-82l-34 34h-48M480 190H316M480 285h-82l-34-34h-48M195 0v65l20 20v29M285 0v65l-20 20v29M195 380v-65l20-20v-29M285 380v-65l-20-20v-29" />
          <path d="M0 145h64l20 20h80M0 235h64l20-20h80M480 145h-64l-20 20h-80M480 235h-64l-20-20h-80" />
          {[129, 153, 177, 201, 225, 249].map(y => <path key={y} d={`M152 ${y}h18m140 0h18`} />)}
          {[179, 203, 227, 251, 275, 299].map(x => <path key={x} d={`M${x} 100v18m0 144v18`} />)}
          <circle cx="64" cy="145" r="3" className="trace-node" /><circle cx="416" cy="235" r="3" className="trace-node" />
          <circle cx="82" cy="95" r="3" className="trace-node" /><circle cx="398" cy="285" r="3" className="trace-node" />
        </g>
        <rect x="164" y="114" width="152" height="152" rx="8" className="chip-outline" />
        <rect x="177" y="127" width="126" height="126" rx="3" className="chip-body" />
        <rect x="186" y="136" width="108" height="108" rx="1" stroke="white" strokeOpacity="0.16" />
        <text x="240" y="195" textAnchor="middle" className="chip-monogram">RP</text>
        <text x="240" y="220" textAnchor="middle" className="chip-subtitle">DESIGN / BUILD</text>
        <g className="diagram-label">
          <rect x="17" y="53" width="83" height="27" rx="3" /><text x="58" y="70" textAnchor="middle">RTL DESIGN</text>
          <rect x="365" y="53" width="98" height="27" rx="3" /><text x="414" y="70" textAnchor="middle">C++ / SYSTEMS</text>
          <rect x="17" y="304" width="103" height="27" rx="3" /><text x="68" y="321" textAnchor="middle">VERIFICATION</text>
          <rect x="362" y="304" width="101" height="27" rx="3" /><text x="412" y="321" textAnchor="middle">EMBEDDED SW</text>
        </g>
      </svg>
      <div className="diagram-footer"><span className="small-cross">+</span><span>Architecture. Implementation. Integration.</span><span className="small-cross">+</span></div>
    </div>
  );
}

function Pipeline() {
  return (
    <div className="pipeline" role="img" aria-label="Five-stage RISC-V pipeline: instruction fetch, decode, execute, memory, and writeback, with data forwarding and hazard control.">
      <div className="pipeline-label">RV32I / 5-STAGE PIPELINE</div>
      <div className="pipeline-stages" aria-hidden="true">
        {['IF', 'ID', 'EX', 'MEM', 'WB'].map((stage, index) => <React.Fragment key={stage}><span className="pipeline-stage">{stage}</span>{index < 4 && <span className="pipeline-connector" />}</React.Fragment>)}
      </div>
      <div className="forwarding-line" aria-hidden="true"><span>Forwarding & hazard control</span></div>
    </div>
  );
}

function ProjectCard({ project }) {
  return (
    <article className={`project-card${project.featured ? ' featured-project' : ''}`}>
      <div className="project-copy">
        <div className="project-eyebrow"><span>{project.number} / {project.discipline}</span>{project.featured && <span className="featured-label">Featured project</span>}</div>
        <h3>{project.title}</h3>
        <p>{project.description}</p>
        <ul className="tag-list" aria-label="Technologies">{project.tags.map(tag => <li key={tag}>{tag}</li>)}</ul>
        <div className="project-links">
          {project.links.length ? project.links.map(link => <ExternalLink key={link.href} href={link.href}>{link.label}</ExternalLink>) : <span className="project-detail">{project.detail}</span>}
        </div>
      </div>
      {project.featured && <Pipeline />}
    </article>
  );
}

const filters = [
  { id: 'all', label: 'All work' },
  { id: 'hardware', label: 'Silicon & hardware' },
  { id: 'software', label: 'Software & systems' }
];

function App() {
  const [filter, setFilter] = useState('all');
  const [menuOpen, setMenuOpen] = useState(false);
  const visibleProjects = projects.filter(project => filter === 'all' || project.categories.includes(filter));

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        document.getElementById('menu-toggle')?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [menuOpen]);

  return (
    <div className="App">
      <a className="skip-link" href="#main">Skip to content</a>
      <header className="site-header">
        <div className="header-inner container">
          <a href="#home" className="brand" aria-label="Romil Patel, home" onClick={() => setMenuOpen(false)}><span className="brand-mark">rp<span>.</span></span><span className="brand-name">Romil Patel</span></a>
          <button id="menu-toggle" className="menu-toggle" aria-expanded={menuOpen} aria-controls="main-navigation" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? 'Close' : 'Menu'}<span aria-hidden="true">{menuOpen ? '−' : '+'}</span></button>
          <nav id="main-navigation" className="main-navigation" aria-label="Main navigation" data-open={menuOpen}>
            <a href="#projects" onClick={() => setMenuOpen(false)}>Projects</a>
            <a href="#experience" onClick={() => setMenuOpen(false)}>Experience</a>
            <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
            <a className="nav-resume" href="#resumes" onClick={() => setMenuOpen(false)}>Resumes<Arrow /></a>
          </nav>
        </div>
      </header>

      <main id="main" tabIndex="-1">
        <section id="home" className="hero container" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="eyebrow"><span className="eyebrow-line" />COMPUTER ENGINEERING / GEORGIA TECH</p>
            <h1 id="hero-title">From silicon<br />to <span>software.</span></h1>
            <div className="hero-introduction">
              <img
                className="hero-portrait"
                src={assetUrl('IMG_9411.JPEG')}
                alt="Romil Patel"
                width="112"
                height="128"
                fetchPriority="high"
                decoding="async"
              />
              <p className="hero-description">I'm Romil Patel. I design digital hardware and build the software that brings systems to life.</p>
            </div>
            <p className="hero-detail">My work spans RISC-V processors, FPGA peripherals, embedded systems, and C++ radar simulation.</p>
            <div className="hero-actions"><a className="button button-primary" href="#projects">Explore my work<Arrow /></a><a className="button button-secondary" href="mailto:romil.patel@gatech.edu">Get in touch<Arrow diagonal /></a></div>
            <div className="hero-location"><span className="location-marker" aria-hidden="true" />Atlanta, GA<span className="location-divider">/</span>B.S. Computer Engineering</div>
          </div>
          <SystemIllustration />
        </section>

        <div className="focus-strip container" aria-label="Engineering focus">
          <div><span className="focus-number">01</span><span>RTL design & verification</span></div>
          <div><span className="focus-number">02</span><span>Systems & embedded software</span></div>
          <div><span className="focus-number">03</span><span>Hardware–software integration</span></div>
        </div>

        <section id="projects" className="section container" aria-labelledby="projects-title">
          <div className="section-heading"><div><p className="eyebrow">01 / SELECTED WORK</p><h2 id="projects-title">Designed. Built. Debugged.</h2></div><p>A closer look at the hardware and software<br className="desktop-break" /> I've worked on.</p></div>
          <div className="project-toolbar"><div className="project-filters" role="group" aria-label="Filter projects">{filters.map(item => <button key={item.id} aria-pressed={filter === item.id} onClick={() => setFilter(item.id)}>{item.label}</button>)}</div><p className="project-count" role="status">{visibleProjects.length} projects</p></div>
          <div className="projects-grid">{visibleProjects.map(project => <ProjectCard key={project.number} project={project} />)}</div>
          <div className="work-footer"><span>More code, experiments, and coursework.</span><ExternalLink href="https://github.com/Rpatel23648">Find me on GitHub</ExternalLink></div>
        </section>

        <section id="experience" className="experience-section" aria-labelledby="experience-title">
          <div className="container section experience-layout">
            <div className="experience-intro"><p className="eyebrow">02 / EXPERIENCE</p><h2 id="experience-title">Engineering<br /> in practice.</h2><p>From distributed radar test systems to aircraft controls and campus infrastructure.</p><a className="text-link" href="#resumes">View my resumes<Arrow /></a></div>
            <div className="experience-list">{experience.map(role => <article className="experience-item" key={role.organization}><div className="experience-meta"><span>{role.date}</span><span>{role.location}</span></div><h3>{role.title}</h3><p className="organization">{role.organization}</p><ul className="experience-points">{role.points.map(point => <li key={point}>{point}</li>)}</ul>{role.metrics && <div className="experience-metrics">{role.metrics.map(metric => <div key={metric.value}><strong>{metric.value}</strong><span>{metric.label}</span></div>)}</div>}</article>)}</div>
          </div>
        </section>

        <section id="about" className="section container" aria-labelledby="about-title">
          <div className="section-heading"><div><p className="eyebrow">03 / BACKGROUND & TOOLKIT</p><h2 id="about-title">A systems perspective.</h2></div></div>
          <div className="about-layout">
            <div className="about-copy"><div className="profile-line"><img src={assetUrl('IMG_9411.JPEG')} alt="Romil Patel" width="80" height="80" loading="lazy" /><div><strong>Romil Patel</strong><span>Computer Engineering at Georgia Tech</span></div></div><p>I'm interested in the decisions that connect hardware architecture to software behavior: how a pipeline handles a hazard, how a peripheral exposes its registers, and how software communicates across a distributed system.</p><p>At Georgia Tech, I'm building that foundation through digital design, computer architecture, operating systems, and embedded systems coursework, alongside hands-on engineering projects.</p><div className="education-note"><span className="eyebrow">EDUCATION</span><h3>Georgia Institute of Technology</h3><p>B.S. in Computer Engineering</p><span className="education-date">August 2024 – Present · Atlanta, GA</span></div></div>
            <div className="skills-list">{skillGroups.map((group, index) => <div className="skill-group" key={group.title}><div className="skill-heading"><span className="focus-number">0{index + 1}</span><h3>{group.title}</h3></div><ul className="skill-terms">{group.items.map(item => <li key={item}>{item}</li>)}</ul></div>)}</div>
          </div>
        </section>

        <section id="resumes" className="resume-section container" aria-labelledby="resumes-title">
          <div className="section-heading"><div><p className="eyebrow">04 / RESUMES</p><h2 id="resumes-title">Two disciplines. One foundation.</h2></div><p>Choose the resume that fits your team.</p></div>
          <div className="resume-grid">{resumes.map(resume => <article className="resume-card" key={resume.title}><div className="resume-card-top"><span className="eyebrow">{resume.category}</span><span className="file-type">PDF</span></div><h3>{resume.title}</h3><p>{resume.description}</p><div className="resume-actions"><a className="button button-primary" href={assetUrl(resume.file)} download={resume.file.split('/').pop()} aria-label={`Download ${resume.title.toLowerCase()} resume`}>Download resume<Arrow download /></a><ExternalLink href={assetUrl(resume.file)}>View {resume.shortName} PDF</ExternalLink></div></article>)}</div>
        </section>

        <section id="contact" className="contact-section container" aria-labelledby="contact-title"><div><p className="eyebrow">LET'S CONNECT</p><h2 id="contact-title">Have something in mind?</h2><p>I'd be glad to talk about silicon engineering, systems software, or what you're building.</p></div><a className="contact-email" href="mailto:romil.patel@gatech.edu">romil.patel@gatech.edu<Arrow diagonal /></a></section>
      </main>

      <footer className="site-footer container"><span>© {new Date().getFullYear()} Romil Patel</span><div><ExternalLink href="https://github.com/Rpatel23648">GitHub</ExternalLink><ExternalLink href="https://www.linkedin.com/in/patel-romil-engineer">LinkedIn</ExternalLink><a className="text-link" href="#home">Back to top<span aria-hidden="true">↑</span></a></div></footer>
    </div>
  );
}

export default App;
